import React from 'react';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #2c5aa0 0%, #1e3a8a 100%);
  text-align: center;
  color: white;
  margin-bottom: 60px;
`;

const Title = styled.h1`
  font-size: 42px;
  margin-bottom: 20px;
  font-weight: 700;
  padding-top: 80px;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.3rem;
  margin-bottom: 40px;
  opacity: 0.9;
  max-width: 800px;
  padding-bottom: 80px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 50px 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    margin: 0 15px;
    padding: 30px 20px;
  }
`;

const SectionTitle = styled.h2`
  color: #2c5aa0;
  font-size: 2.2rem;
  margin-bottom: 30px;
  text-align: center;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: #2c5aa0;
  }
`;

const WorshipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  margin-top: 50px;
`;

const WorshipCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const WorshipIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 20px;
`;

const WorshipTitle = styled.h3`
  color: #2c5aa0;
  font-size: 1.5rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const WorshipTime = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 10px;
  font-weight: 500;
`;

const WorshipDescription = styled.p`
  color: #666;
  line-height: 1.6;
  font-size: 0.95rem;
`;

const OnlineWorshipSection = styled.div`
  background: #fff;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
  margin-top: 50px;

  h3 {
    font-size: 24px;
    color: #2c5aa0;
    margin-bottom: 20px;
    font-weight: 600;
  }

  p {
    font-size: 16px;
    color: #666;
    margin-bottom: 25px;
  }

  .youtube-link {
    display: inline-block;
    background: #ff0000;
    color: #fff;
    padding: 15px 30px;
    border-radius: 25px;
    text-decoration: none;
    font-weight: 600;
    font-size: 16px;
    transition: background 0.3s ease;

    &:hover {
      background: #cc0000;
    }
  }
`;


const InfoSection = styled.div`
  background: #e3f2fd;
  padding: 40px;
  border-radius: 15px;
  margin-top: 50px;
`;

const InfoTitle = styled.h3`
  color: #1976d2;
  font-size: 1.8rem;
  margin-bottom: 20px;
  text-align: center;
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  max-width: 600px;
  margin: 0 auto;
`;

const InfoItem = styled.li`
  padding: 10px 0;
  border-bottom: 1px solid #bbdefb;
  display: flex;
  align-items: center;
  
  &:last-child {
    border-bottom: none;
  }

  &::before {
    content: '✓';
    color: #1976d2;
    font-weight: bold;
    margin-right: 15px;
    font-size: 1.2rem;
  }
`;

const SundayWorship: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <Title>주일 예배</Title>
        <Subtitle>
          하나님을 예배하며 말씀과 기도로 하나 되는 거룩한 시간입니다
        </Subtitle>
      </HeroSection>

      <ContentSection>
        <SectionTitle>주일 예배 시간</SectionTitle>

        <WorshipGrid>
          <WorshipCard>
            <WorshipIcon>☀️</WorshipIcon>
            <WorshipTitle>주일 예배</WorshipTitle>
            <WorshipTime>주일 오전 11:00</WorshipTime>
            <WorshipDescription>
              온 성도가 함께 모여 하나님께 영광을 돌리는 주일 예배입니다.
            </WorshipDescription>
          </WorshipCard>
          <WorshipCard>
            <WorshipIcon>🙏</WorshipIcon>
            <WorshipTitle>주중새벽기도</WorshipTitle>
            <WorshipTime>화요일 새벽 6:00</WorshipTime>
            <WorshipDescription>
              말씀과 기도로 하루를 시작하는 새벽 기도회입니다.
            </WorshipDescription>
          </WorshipCard>
          <WorshipCard>
            <WorshipIcon>🎵</WorshipIcon>
            <WorshipTitle>금요찬양기도집회</WorshipTitle>
            <WorshipTime>금요일 오후 8:00</WorshipTime>
            <WorshipDescription>
              뜨거운 찬양과 기도로 영적 충만을 경험하는 시간입니다.
            </WorshipDescription>
          </WorshipCard>
          <WorshipCard>
            <WorshipIcon>🧒</WorshipIcon>
            <WorshipTitle>주일학교</WorshipTitle>
            <WorshipTime>주일 오전 11:00</WorshipTime>
            <WorshipDescription>
              다음 세대를 믿음으로 양육하는 어린이 예배입니다.
            </WorshipDescription>
          </WorshipCard>
        </WorshipGrid>

        <OnlineWorshipSection>
          <h3>📺 온라인 예배</h3>
          <p>유튜브를 통해 온라인으로도 예배에 참여하실 수 있습니다.</p>
          <a href="https://www.youtube.com/channel/UC0VeDAbpybpr30PWxhLJwgw" target="_blank" rel="noopener noreferrer" className="youtube-link">
            🎥 얼바인중앙침례교회 유튜브 채널
          </a>
        </OnlineWorshipSection>

        <InfoSection>
          <InfoTitle>예배 참석 안내</InfoTitle>
          <InfoList>
            <InfoItem>예배 10분 전까지 도착하여 기도로 준비합니다</InfoItem>
            <InfoItem>성경과 찬송가를 지참해 주세요</InfoItem>
            <InfoItem>어린이를 위한 별도 프로그램이 준비되어 있습니다</InfoItem>
            <InfoItem>주차 공간이 충분히 마련되어 있습니다</InfoItem>
            <InfoItem>새신자를 위한 환영 시간이 있습니다</InfoItem>
            <InfoItem>온라인 실시간 방송도 진행됩니다</InfoItem>
          </InfoList>
        </InfoSection>
      </ContentSection>
    </PageContainer>
  );
};

export default SundayWorship;
